import { join } from 'node:path';
import type { Request, Response } from 'express';

import { readLogEntriesByTrace, readRecentTraceCalls, readLogFilePage, readServerLogs, readTriggerList, readTriggerDetail, readCapabilityTraceList } from './services';
import {
  getRelativePath,
  parseLimit,
  parsePositiveInt,
  resolveLogFilePath,
  serializeError,
} from './utils';

/**
 * Handle not found error
 */
function handleNotFound(res: Response, filePath: string, message = 'Log file not found'): void {
  res.status(404).json({ message: `${message}: ${getRelativePath(filePath)}` });
}

/**
 * Handle generic error
 */
function handleError(res: Response, error: unknown, message = 'Failed to read log file'): void {
  res.status(500).json({ message, error: serializeError(error) });
}

/**
 * Create handler for getting log entries by trace ID
 */
export function createGetTraceEntriesHandler(logDir: string) {
  const appLogPath = join(logDir, 'server.log');

  return async (req: Request, res: Response) => {
    const traceId = (req.params.traceId || '').trim();
    if (!traceId) {
      return res.status(400).json({ message: 'traceId is required' });
    }

    const limit = parseLimit(req.query.limit as string | undefined, 200, 1000);

    try {
      const entries = await readLogEntriesByTrace(appLogPath, traceId, limit);
      if (!entries) {
        return handleNotFound(res, appLogPath);
      }
      res.json({
        file: getRelativePath(appLogPath),
        traceId,
        count: entries.length,
        entries,
      });
    } catch (error) {
      handleError(res, error);
    }
  };
}

/**
 * Create handler for getting recent trace calls
 */
export function createGetRecentTracesHandler(logDir: string) {
  const traceLogPath = join(logDir, 'trace.log');

  return async (req: Request, res: Response) => {
    const page = parsePositiveInt(req.query.page as string | undefined, 1);
    const pageSize = parseLimit(req.query.pageSize as string | undefined, 10, 100);
    const pathFilter = typeof req.query.path === 'string' ? req.query.path.trim() : undefined;
    const methodFilter = typeof req.query.method === 'string' ? req.query.method.trim().toUpperCase() : undefined;

    try {
      const result = await readRecentTraceCalls(traceLogPath, page, pageSize, pathFilter, methodFilter);
      if (!result) {
        return handleNotFound(res, traceLogPath);
      }
      res.json({
        file: getRelativePath(traceLogPath),
        ...result,
        path: pathFilter || null,
        method: methodFilter || null,
        count: result.calls.length,
      });
    } catch (error) {
      handleError(res, error, 'Failed to read trace log');
    }
  };
}

/**
 * Create handler for getting log file by name
 */
export function createGetLogFileHandler(logDir: string) {
  return async (req: Request, res: Response) => {
    const fileName = (req.params.fileName || '').trim();
    if (!fileName) {
      return res.status(400).json({ message: 'fileName is required' });
    }

    const page = parsePositiveInt(req.query.page as string | undefined, 1);
    const pageSize = parseLimit(req.query.pageSize as string | undefined, 200, 2000);

    try {
      const filePath = resolveLogFilePath(logDir, fileName);
      const result = await readLogFilePage(filePath, page, pageSize);
      if (!result) {
        return handleNotFound(res, filePath);
      }

      res.json({
        file: getRelativePath(filePath),
        ...result,
      });
    } catch (error) {
      handleError(res, error, 'Failed to read specified log file');
    }
  };
}

/**
 * Create handler for getting server logs
 *
 * Reads from 4 log sources:
 * - server.log (Pino JSON)
 * - trace.log (Pino JSON)
 * - server.std.log (plain text)
 * - client.std.log (plain text)
 *
 * Query parameters:
 * - limit: number of logs to return (default: 100, max: 1000)
 * - offset: offset for pagination (default: 0)
 * - levels: comma-separated log levels (e.g., "error,warn")
 * - sources: comma-separated sources (e.g., "server,trace")
 */
export function createGetServerLogsHandler(logDir: string) {
  return async (req: Request, res: Response) => {
    const limit = parseLimit(req.query.limit as string | undefined, 100, 1000);
    const offset = parsePositiveInt(req.query.offset as string | undefined, 0);

    // Parse levels filter
    const levels = req.query.levels
      ? String(req.query.levels).split(',').map(l => l.trim()).filter(Boolean)
      : undefined;

    // Parse sources filter
    const sources = req.query.sources
      ? String(req.query.sources).split(',').map(s => s.trim()).filter(Boolean)
      : undefined;

    try {
      const result = await readServerLogs(logDir, {
        limit,
        offset,
        levels,
        sources,
      });

      if (!result) {
        return res.status(404).json({
          message: 'No server log files found',
          hint: 'Expected files: server.log, trace.log, server.std.log, client.std.log',
        });
      }

      res.json(result);
    } catch (error) {
      handleError(res, error, 'Failed to read server logs');
    }
  }
}
/**
 * Create handler for getting all automation trigger list in trace.log
 * filter by request_body.trigger & limit & path=/__innerapi__/automation/invoke
 */
export function createGetTriggerListHandler(logDir: string) {
  const traceLogPath = join(logDir, 'trace.log');

  return async (req: Request, res: Response) => {
    const trigger = typeof req.query.trigger === 'string' ? req.query.trigger.trim() : undefined;
    if (!trigger) {
      return res.status(400).json({ message: 'trigger is required' });
    }

    const triggerID = typeof req.query.triggerID === 'string' ? req.query.triggerID.trim() : undefined;
    const path = typeof req.query.path === 'string' ? req.query.path.trim() : '/__innerapi__/automation/invoke';
    const limit = parseLimit(req.query.limit as string | undefined, 10, 200);

    try {
      const result = await readTriggerList(traceLogPath, trigger, path, limit, triggerID);
      if (!result) {
        return handleNotFound(res, traceLogPath);
      }
      res.json({
        file: getRelativePath(traceLogPath),
        path,
        ...result,
      });
    } catch (error) {
      handleError(res, error, 'Failed to read trace log');
    }
  };
}

export function createGetTriggerDetailHandler(logDir: string) {
  const traceLogPath = join(logDir, 'server.log');

  return async (req: Request, res: Response) => {
    const instanceID = (req.params.instanceID || '').trim();
    if (!instanceID) {
      return res.status(400).json({ message: 'instanceID is required' });
    }

    const path = typeof req.query.path === 'string' ? req.query.path.trim() : '/__innerapi__/automation/invoke';

    try {
      const result = await readTriggerDetail(traceLogPath, path, instanceID);
      if (!result) {
        return handleNotFound(res, traceLogPath);
      }
      res.json({
        file: getRelativePath(traceLogPath),
        ...result,
      });
    } catch (error) {
      handleError(res, error, 'Failed to read trace log');
    }
  };
}

/**
 * Create handler for getting capability trace list
 * Filter by capability_id in server.log
 *
 * Query parameters:
 * - capability_id: required, the capability ID to filter
 * - limit: number of traces to return (default: 10, max: 200)
 */
export function createGetCapabilityTraceListHandler(logDir: string) {
  const serverLogPath = join(logDir, 'server.log');

  return async (req: Request, res: Response) => {
    const capabilityId = typeof req.query.capability_id === 'string' ? req.query.capability_id.trim() : undefined;
    if (!capabilityId) {
      return res.status(400).json({ message: 'capability_id is required' });
    }

    const limit = parseLimit(req.query.limit as string | undefined, 10, 200);

    try {
      const result = await readCapabilityTraceList(serverLogPath, capabilityId, limit);
      if (!result) {
        return handleNotFound(res, serverLogPath);
      }
      res.json({
        file: getRelativePath(serverLogPath),
        ...result,
      });
    } catch (error) {
      handleError(res, error, 'Failed to read server log');
    }
  };
}